import router from '../router'

class ServiceResponse {
  constructor(data, success, message) {
    this.Data = data
    this.Success = success
    this.Message = message
  }
}

class ApiService {
  constructor() {
    this.apiBaseUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:8000/api/v1'
    this.defaultHeaders = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  }

  // FIXED: Get auth token from multiple possible storage keys
  getAuthToken() {
    // Check all possible token storage locations in priority order
    const tokenKeys = [
      'access_token',  // Most common for Laravel Passport
      'auth_token',    // Original key
      'token',         // Common alternative
      'authToken',     // camelCase version
      'bearer_token'   // Another alternative
    ]

    for (const key of tokenKeys) {
      const token = localStorage.getItem(key)
      if (token && typeof token === 'string' && token.length > 10) {
        return token
      }
    }

    return null
  }

  // FIXED: Set auth token with better validation and cleanup
  setAuthToken(token, tokenKey = 'access_token') {

    if (token && typeof token === 'string' && token.length > 10) {
      // Clear all possible existing tokens first
      this.clearAllTokens()

      // Set the new token in multiple locations for compatibility
      localStorage.setItem('access_token', token) // Primary for Laravel Passport
      localStorage.setItem('token', token)        // Backup
      localStorage.setItem('auth_token', token)   // Legacy
      localStorage.setItem(tokenKey, token)       // Requested key


      // Verify the token was stored correctly
      const storedToken = localStorage.getItem('access_token')
      if (storedToken !== token) {
        console.error('Token storage verification failed!')
        throw new Error('Failed to store token correctly')
      }

    } else if (token === null || token === undefined || token === '') {
      this.clearAllTokens()
    } else {
      console.error('Invalid token provided to setAuthToken:', {
        token,
        type: typeof token,
        length: typeof token === 'string' ? token.length : 'N/A'
      })
      throw new Error(`Invalid token: expected string with length > 10, got ${typeof token} with length ${typeof token === 'string' ? token.length : 'N/A'}`)
    }
  }

  // FIXED: Clear all possible token storage locations
  clearAllTokens() {
    const tokenKeys = [
      'auth_token', 'token', 'access_token', 'authToken', 'bearer_token',
      'user_data', 'user_id', 'jwt_expiry',
      'impersonation_active', 'impersonation_data', 'original_user_data'
    ]

    tokenKeys.forEach(key => {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key) // Also clear from sessionStorage
    })
  }

  // Get default headers with auth token
  getHeaders(customHeaders = {}) {
    const headers = { ...this.defaultHeaders, ...customHeaders }
    const token = this.getAuthToken()

    if (token) {
      // CRITICAL: Ensure proper Bearer format
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  // FIXED: Enhanced unauthenticated handling
  handleUnauthenticated(jsonData, response = null) {
    const isUnauthenticated = jsonData && (
      jsonData.message === 'Unauthenticated' ||
      jsonData.message === 'Token has expired' ||
      jsonData.message === 'Invalid token' ||
      jsonData.error === 'Unauthorized' ||
      (response && response.status === 401)
    )

    if (isUnauthenticated) {

      // Clear all stored auth data
      this.clearAllTokens()

      // Only redirect if we're not already on a public page
      const currentPath = router.currentRoute.value.path
      const publicPaths = [
        '/',
        '/about',
        '/our-fellows',
        '/public-news',
        '/public-gallery',
        '/network-of-excellence',
        '/funding-and-training',
        '/contact',
        '/login',
        '/register',
        '/forgot-password',
        '/unauthorized'
      ]

      if (!publicPaths.includes(currentPath)) {
        router.push({
          path: '/login',
          query: { redirect: currentPath }
        })
      }
      return true
    }
    return false
  }

  // Helper method to build query string
  buildQueryString(params) {
    if (!params || Object.keys(params).length === 0) {
      return ''
    }

    const searchParams = new URLSearchParams()
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        searchParams.append(key, params[key])
      }
    })

    return '?' + searchParams.toString()
  }

  async get(endpoint, queryParams = {}, customHeaders = {}) {
    const headers = new Headers(this.getHeaders(customHeaders))

    const queryString = this.buildQueryString(queryParams)
    const fullUrl = this.apiBaseUrl + endpoint + queryString

    const requestOptions = {
      method: 'GET',
      headers: headers
    }

    try {
      const response = await fetch(fullUrl, requestOptions)
      const jsonData = await response.json()

      if (!response.ok) {
        if (response.status === 401) {
          if (this.handleUnauthenticated(jsonData, response)) {
            return new ServiceResponse(null, false, "Unauthenticated")
          }
        }

        if (response.status === 500 || response.status === 404) {
          throw new Error(`API request failed with status: ${response.status}`)
        }
      }

      // Check for unauthenticated message in successful responses too
      if (this.handleUnauthenticated(jsonData, response)) {
        return new ServiceResponse(null, false, "Unauthenticated")
      }

      return new ServiceResponse(jsonData, true, null)

    } catch (error) {
      console.error(`GET ${endpoint} error:`, error)
      return new ServiceResponse(null, false, error.message || error.Message)
    }
  }

  async post(endpoint, customHeaders = {}, parameters = {}) {
    const requestOptions = {
      method: 'POST'
    };

    // Handle FormData (keep existing behavior)
    if (parameters instanceof FormData) {
      const token = this.getAuthToken();
      const headers = {
        'Accept': 'application/json'
      };

      if (token) {
        const bearerToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        headers['Authorization'] = bearerToken;
      }

      requestOptions.headers = headers;
      requestOptions.body = parameters;

    } else {
      // Normal JSON POST (unchanged behavior)
      requestOptions.headers = this.getHeaders(customHeaders);
      requestOptions.body = JSON.stringify(parameters);
    }

    const fullUrl = this.apiBaseUrl + endpoint;

    // ✅ NEW: SAFELY HANDLE BLOB EXPORTS (Excel, PDF, Images, etc.)
    if (customHeaders?.responseType === 'blob') {
      const response = await fetch(fullUrl, requestOptions);
      return response; // return raw blob, skip JSON parsing
    }

    // 📌 BELOW THIS LINE NOTHING CHANGED (JSON flow remains identical)
    try {
      const response = await fetch(fullUrl, requestOptions);
      const jsonData = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          if (this.handleUnauthenticated(jsonData, response)) {
            return new ServiceResponse(null, false, "Unauthenticated");
          }
        }

        if (response.status === 422) {
          return new ServiceResponse(jsonData, false, jsonData.message || "Validation failed");
        }

        if (response.status === 500 || response.status === 404) {
          console.error(`POST ${endpoint} failed:`, {
            status: response.status,
            statusText: response.statusText,
            data: jsonData
          });
          throw new Error(`API request failed with status: ${response.status}`);
        }
      }

      if (this.handleUnauthenticated(jsonData, response)) {
        return new ServiceResponse(null, false, "Unauthenticated");
      }

      return new ServiceResponse(jsonData, true, null);

    } catch (error) {
      console.error(`POST ${endpoint} error:`, error);
      return new ServiceResponse(null, false, error.message || error.Message);
    }
  }

  // POST a FormData body and report upload progress (fetch cannot). Resolves
  // to the same ServiceResponse shape as post(), but any non-2xx status is a
  // failure carrying the server's message, so callers can show it per file.
  postFormWithProgress(endpoint, formData, onProgress) {
    return new Promise(resolve => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', this.apiBaseUrl + endpoint)
      xhr.setRequestHeader('Accept', 'application/json')

      const token = this.getAuthToken()
      if (token) {
        xhr.setRequestHeader('Authorization', token.startsWith('Bearer ') ? token : `Bearer ${token}`)
      }

      if (typeof onProgress === 'function' && xhr.upload) {
        xhr.upload.onprogress = event => {
          if (event.lengthComputable && event.total > 0) {
            onProgress((event.loaded / event.total) * 100)
          }
        }
      }

      xhr.onerror = () => resolve(new ServiceResponse(null, false, 'Network error, check your connection and retry'))
      xhr.onabort = () => resolve(new ServiceResponse(null, false, 'Upload cancelled'))

      xhr.onload = () => {
        let jsonData = null
        try {
          jsonData = JSON.parse(xhr.responseText)
        } catch {
          jsonData = null
        }

        const status = xhr.status

        if (status === 401 && this.handleUnauthenticated(jsonData || {}, { status })) {
          return resolve(new ServiceResponse(null, false, 'Unauthenticated'))
        }

        if (status >= 200 && status < 300) {
          return resolve(new ServiceResponse(jsonData, true, null))
        }

        let message = jsonData?.Message || jsonData?.message || `Upload failed (HTTP ${status})`
        if (status === 413) {
          message = 'File is too large for the server'
        } else if (status === 429) {
          message = 'Too many requests, please retry'
        } else if (status === 422 && jsonData?.errors) {
          const firstError = Object.values(jsonData.errors).flat()[0]
          if (firstError) message = firstError
        }

        resolve(new ServiceResponse(jsonData, false, message))
      }

      xhr.send(formData)
    })
  }

  // Generic PUT request
  async put(endpoint, customHeaders = {}, parameters = {}) {
    const headers = new Headers(this.getHeaders(customHeaders))
    const jsonParamData = JSON.stringify(parameters)

    const requestOptions = {
      method: 'PUT',
      headers: headers,
      body: jsonParamData
    }

    try {
      const fullUrl = this.apiBaseUrl + endpoint

      const response = await fetch(fullUrl, requestOptions)
      const jsonData = await response.json()

      if (!response.ok) {
        if (response.status === 401) {
          if (this.handleUnauthenticated(jsonData, response)) {
            return new ServiceResponse(null, false, "Unauthenticated")
          }
        }

        if (response.status === 500 || response.status === 404) {
          throw new Error(`API request failed with status: ${response.status}`)
        }
      }

      // Check for unauthenticated message in successful responses too
      if (this.handleUnauthenticated(jsonData, response)) {
        return new ServiceResponse(null, false, "Unauthenticated")
      }

      return new ServiceResponse(jsonData, true, null)

    } catch (error) {
      console.error(`PUT ${endpoint} error:`, error)
      return new ServiceResponse(null, false, error.message || error.Message)
    }
  }

  // Generic DELETE request
  async delete(endpoint, customHeaders = {}) {
    const headers = new Headers(this.getHeaders(customHeaders))

    const requestOptions = {
      method: 'DELETE',
      headers: headers
    }

    try {
      const fullUrl = this.apiBaseUrl + endpoint

      const response = await fetch(fullUrl, requestOptions)
      const jsonData = await response.json()

      if (!response.ok) {
        if (response.status === 401) {
          if (this.handleUnauthenticated(jsonData, response)) {
            return new ServiceResponse(null, false, "Unauthenticated")
          }
        }

        if (response.status === 500 || response.status === 404) {
          throw new Error(`API request failed with status: ${response.status}`)
        }
      }

      // Check for unauthenticated message in successful responses too
      if (this.handleUnauthenticated(jsonData, response)) {
        return new ServiceResponse(null, false, "Unauthenticated")
      }

      return new ServiceResponse(jsonData, true, null)

    } catch (error) {
      console.error(`DELETE ${endpoint} error:`, error)
      return new ServiceResponse(null, false, error.message || error.Message)
    }
  }

  // File upload method
  async uploadFile(endpoint, file, additionalData = {}) {
    const formData = new FormData()
    formData.append('file', file)

    // Add additional form data
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key])
    })

    const headers = this.getHeaders()
    delete headers['Content-Type'] // Let browser set it for FormData

    const requestOptions = {
      method: 'POST',
      headers: new Headers(headers),
      body: formData
    }

    try {
      const response = await fetch(this.apiBaseUrl + endpoint, requestOptions)
      const jsonData = await response.json()

      if (!response.ok) {
        if (response.status === 401) {
          if (this.handleUnauthenticated(jsonData, response)) {
            return new ServiceResponse(null, false, "Unauthenticated")
          }
        }

        if (response.status === 500 || response.status === 404) {
          throw new Error(`API request failed with status: ${response.status}`)
        }
      }

      return new ServiceResponse(jsonData, true, null)

    } catch (error) {
      return new ServiceResponse(null, false, error.message || error.Message)
    }
  }

  async uploadAvatar(endpoint, file) {
    const formData = new FormData()
    formData.append('avatar', file)

    const headers = this.getHeaders()
    delete headers['Content-Type']

    const requestOptions = {
      method: 'POST',
      headers: new Headers(headers),
      body: formData
    }

    try {

      const response = await fetch(this.apiBaseUrl + endpoint, requestOptions)
      const jsonData = await response.json()

      if (!response.ok) {
        if (response.status === 401) {
          if (this.handleUnauthenticated(jsonData, response)) {
            return new ServiceResponse(null, false, "Unauthenticated")
          }
        }
        throw new Error(`API request failed with status: ${response.status}`)
      }

      return new ServiceResponse(jsonData, true, null)

    } catch (error) {
      console.error('Avatar upload error:', error)
      return new ServiceResponse(null, false, error.message || error.Message)
    }
  }

  async uploadAvatarBase64(endpoint, base64DataUrl) {
    const headers = this.getHeaders()

    const requestOptions = {
      method: 'POST',
      headers: new Headers(headers),
      body: JSON.stringify({ avatar: base64DataUrl })
    }

    try {
      const response = await fetch(this.apiBaseUrl + endpoint, requestOptions)
      const jsonData = await response.json()

      if (!response.ok) {
        if (response.status === 401) {
          if (this.handleUnauthenticated(jsonData, response)) {
            return new ServiceResponse(null, false, 'Unauthenticated')
          }
        }
        throw new Error(`API request failed with status: ${response.status}`)
      }

      return new ServiceResponse(jsonData, true, null)
    } catch (error) {
      console.error('Avatar base64 upload error:', error)
      return new ServiceResponse(null, false, error.message || error.Message)
    }
  }
}

export default new ApiService()
